import React, { ReactNode, useState, useRef } from 'react';
import '../../styles/components/list.scss';
import { Input } from '../input';
import { Cross, Search } from '../svgs';
import { setClass } from '@utils';

interface IListProps<T> {
  right?: number;
  top?: number;
  itemsMaxHeight?: number;
  className?: string;
  items: T[];
  headerTitle?: string;
  isCanFilter?: boolean;
  anchor?: React.MutableRefObject<HTMLDivElement>;
  editMeeting?: boolean;
  isCanResetChoosedOption?: boolean;
  placeholder?: string;
  resetFilter?: boolean;
  onResetChoosedOptions?: () => void;
  itemForFilter?: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onClose?: () => void;
  sortingRule: (a: T, b: T) => number;
  onEnterPress?: (item: T) => void;
}
/**
 * @right - значение координаты right
 * @top - значение координаты top
 * @itemsMaxHeight - максимальная высота блока с items
 * @className - кастомный класс для компонента list-component
 * @items - массив элементов списка
 * @headerTitle - опциональное поле. Принимает строку, которую выводит как закоголовок "шапки" с функцией закрытия на крестик
 * @isCanFilter - флаг, отвечающий за отображение в футере поиска и фильтрации items по введенному значению
 * @itemForFilter - функция, принимающая item и возвращающая значение, в котором надо искать (н-р, (item)=> item.name)
 * @renderItem - шаблон вывода item
 * @onClose - событие при закрытии List при нажатии на крестик в шапке. Актуально, если в компонент передается также headerTitle
 */
export const List = <T,>({
  right,
  top,
  itemsMaxHeight,
  className,
  items,
  headerTitle,
  isCanFilter,
  anchor,
  editMeeting = false,
  isCanResetChoosedOption,
  placeholder,
  onEnterPress,
  onResetChoosedOptions,
  itemForFilter,
  renderItem,
  onClose,
  sortingRule,
  resetFilter = false,
}: IListProps<T>) => {

  const [filteredListItems, setFilteredListItems] = useState(items);
  const [filterValue, setFilterValue] = useState('');
  const filterRef = useRef<HTMLInputElement>();

  const onChangeFilterValue = (value: string) => {
    setFilterValue(value);
    setFilteredListItems(items.filter(item =>
      itemForFilter(item)
        .toLocaleLowerCase()
        .includes(value.toLocaleLowerCase().trim())
    ));
  };

  const resetFilterValue = () => {
    if (resetFilter) {
      onChangeFilterValue('');
      filterRef.current?.focus();
    }
  };

  const style = top || right ? { top, right } : {};

  const onSelectRemaining = (e: React.KeyboardEvent) => {    
    if (e.key === 'Enter' && filteredListItems.length === 1) {
      onEnterPress(filteredListItems[0]);
      resetFilterValue();
    }
  }

  return (
    <div
      className={setClass('component-list', className && className)}
      style={style}
      ref={anchor}
    >
      {editMeeting && <div className='underline' />}

      {headerTitle && <div className="header-title">
        {headerTitle}
        <div className="actions">
          {isCanResetChoosedOption && (
            <div
              className="reset-choosed-option"
              onClick={onResetChoosedOptions}
            >
              Сбросить
            </div>
          )}
          <Cross onClick={onClose} />
        </div>
      </div>}
      {isCanFilter && (
        <div 
          className="search" 
          onKeyDown={onSelectRemaining}
        >
          <Input
            filterRef={filterRef}
            classes='search-input'
            variant='empty'
            value={filterValue}
            placeholder={'Поиск'}
            startIcon={<Search />}
            onChange={onChangeFilterValue}
            autoFocus
          />
        </div>
      )}
      <div className="items" style={{ maxHeight: itemsMaxHeight }}>
        {!!filteredListItems.length
          ? filteredListItems.sort(sortingRule).map(item => <div onClick={resetFilterValue}>{renderItem(item)}</div>)
          : <div className='placeholder'>{placeholder}</div>
        }
      </div>
    </div>
  );
}

